import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getAdminArticle, updateArticle, uploadImage, generateSlug } from '../../api';

function AdminEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        meta_title: '',
        meta_description: '',
        cover_image_url: '',
        content_html: '',
        category: '',
        tags: '',
        status: 0,
        published_at: ''
    });
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [articleInfo, setArticleInfo] = useState(null);

    const loadArticle = useCallback(async () => {
        try {
            const res = await getAdminArticle(id);
            const article = res.data;
            
            setFormData({
                title: article.title || '',
                slug: article.slug || '',
                meta_title: article.meta_title || '',
                meta_description: article.meta_description || '',
                cover_image_url: article.cover_image_url || '',
                content_html: article.content_html || '',
                category: article.category || '',
                tags: article.tags || '',
                status: article.status || 0,
                published_at: article.published_at ? article.published_at.slice(0, 16) : ''
            });
            
            setArticleInfo({
                created_at: article.created_at,
                updated_at: article.updated_at
            });
        } catch (err) {
            console.error('Error loading article:', err);
            setError('Articolo non trovato');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadArticle();
    }, [loadArticle]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleContentChange = (content) => {
        setFormData(prev => ({ ...prev, content_html: content }));
    };

    const handleGenerateSlug = async () => {
        if (!formData.title) return;
        try {
            const res = await generateSlug(formData.title);
            setFormData(prev => ({ ...prev, slug: res.data.slug }));
        } catch (err) {
            console.error('Error generating slug:', err);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Preview
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target.result);
        reader.readAsDataURL(file);

        // Upload
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('image', file);
            const res = await uploadImage(formDataUpload);
            setFormData(prev => ({ ...prev, cover_image_url: res.data.url }));
        } catch (err) {
            console.error('Error uploading image:', err);
            setError('Errore nel caricamento dell\'immagine');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            const dataToSend = {
                ...formData,
                status: parseInt(formData.status),
                published_at: formData.published_at || null
            };
            
            await updateArticle(id, dataToSend);
            navigate('/admin/blog');
        } catch (err) {
            console.error('Error updating article:', err);
            setError(err.response?.data?.error || 'Errore nell\'aggiornamento dell\'articolo');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
        ]
    };

    if (loading) {
        return (
            <>
                <header className="admin-header">
                    <h1>Modifica Articolo</h1>
                </header>
                <div className="admin-content">
                    <div className="loading">Caricamento...</div>
                </div>
            </>
        );
    }

    if (error && !formData.title) {
        return (
            <>
                <header className="admin-header">
                    <h1>Modifica Articolo</h1>
                </header>
                <div className="admin-content">
                    <div className="alert alert-error">{error}</div>
                    <Link to="/admin/blog" className="btn btn-secondary">Torna alla lista</Link>
                </div>
            </>
        );
    }

    return (
        <>
            <header className="admin-header">
                <h1>Modifica Articolo</h1>
            </header>

            <div className="admin-content">
                {error && (
                    <div className="alert alert-error">{error}</div>
                )}

                <form onSubmit={handleSubmit} className="article-form">
                    <div className="form-grid">
                        <div className="form-main">
                            <div className="form-group">
                                <label htmlFor="title">Titolo *</label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                    className="form-control"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="slug">Slug URL *</label>
                                <div className="input-group">
                                    <span className="input-prefix">/blog/</span>
                                    <input
                                        type="text"
                                        id="slug"
                                        name="slug"
                                        value={formData.slug}
                                        onChange={handleChange}
                                        required
                                        className="form-control"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleGenerateSlug}
                                        className="btn btn-secondary"
                                    >
                                        Genera
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Contenuto</label>
                                <ReactQuill
                                    value={formData.content_html}
                                    onChange={handleContentChange}
                                    modules={quillModules}
                                    theme="snow"
                                />
                            </div>
                        </div>

                        <div className="form-sidebar">
                            <div className="sidebar-section">
                                <h3>Pubblicazione</h3>
                                
                                <div className="form-group">
                                    <label htmlFor="status">Stato</label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        className="form-control"
                                    >
                                        <option value={0}>Bozza</option>
                                        <option value={1}>Pubblicato</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="published_at">Data Pubblicazione</label>
                                    <input
                                        type="datetime-local"
                                        id="published_at"
                                        name="published_at"
                                        value={formData.published_at}
                                        onChange={handleChange}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="sidebar-section">
                                <h3>Immagine Copertina (1200 x 800 px)</h3>
                                
                                <div className="form-group">
                                    <div className="image-preview">
                                        {imagePreview || formData.cover_image_url ? (
                                            <img
                                                src={imagePreview || `${formData.cover_image_url}`}
                                                alt="Preview"
                                            />
                                        ) : (
                                            <span className="placeholder">Nessuna immagine</span>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="form-control"
                                    />
                                    <small style={{ color: '#666', fontSize: '0.8rem' }}>
                                        Lascia vuoto per mantenere l'immagine attuale
                                    </small>
                                </div>
                            </div>

                            <div className="sidebar-section">
                                <h3>Categorizzazione</h3>
                                
                                <div className="form-group">
                                    <label htmlFor="category">Categoria</label>
                                    <input
                                        type="text"
                                        id="category"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className="form-control"
                                        placeholder="es. Ricambi, Guide, News"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="tags">Tags (separati da virgola)</label>
                                    <input
                                        type="text"
                                        id="tags"
                                        name="tags"
                                        value={formData.tags}
                                        onChange={handleChange}
                                        className="form-control"
                                        placeholder="es. moto, ricambi, honda"
                                    />
                                </div>
                            </div>

                            <div className="sidebar-section">
                                <h3>SEO</h3>
                                
                                <div className="form-group">
                                    <label htmlFor="meta_title">Meta Title (max 70 caratteri)</label>
                                    <input
                                        type="text"
                                        id="meta_title"
                                        name="meta_title"
                                        value={formData.meta_title}
                                        onChange={handleChange}
                                        maxLength={70}
                                        className="form-control"
                                    />
                                    <span className="char-count">{formData.meta_title.length}/70</span>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="meta_description">Meta Description (max 160 caratteri)</label>
                                    <textarea
                                        id="meta_description"
                                        name="meta_description"
                                        value={formData.meta_description}
                                        onChange={handleChange}
                                        maxLength={160}
                                        className="form-control"
                                        rows={3}
                                    />
                                    <span className="char-count">{formData.meta_description.length}/160</span>
                                </div>
                            </div>

                            {articleInfo && (
                                <div className="sidebar-section">
                                    <h3>Info</h3>
                                    <p style={{ fontSize: '0.875rem', marginBottom: '8px' }}>
                                        <strong>Creato:</strong> {formatDate(articleInfo.created_at)}
                                    </p>
                                    <p style={{ fontSize: '0.875rem' }}>
                                        <strong>Modificato:</strong> {formatDate(articleInfo.updated_at)}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-actions">
                        <Link to="/admin/blog" className="btn btn-secondary">Annulla</Link>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Salvataggio...' : 'Aggiorna Articolo'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
}

export default AdminEdit;
